import { StylesheetType } from '@common/flare-data/enums/stylesheet-type.enum';
import { array } from '@common/util/array';
import { isAbsoluteUrl, replaceBackslashes, resolvePath } from '@common/util/path';
import * as cssTree from 'css-tree';
import { AnyNode, AtRule, ChildNode, Comment, Declaration, Root, Rule, stringify } from 'postcss';
import scss from 'postcss-scss';

/** Regular expression to match Flare css meta comment in a stylesheet content */
const cssMetaRegExp = /\/\*\s*<meta(\s*|(\s+([^/*][^*]*)*))\/>\s*\*+\//m;
/** Regular expression to match Flare branding comment in a stylesheet content */
const brandingRegExp = /\/\*\s*MadCap-Branding[^*]*\*+([^/*][^*]*\*+)*\//m;
/** Regular expression to match Flare table comment in a stylesheet content */
const tableRegExp = /\/\*\s*MadCap\s+Table\s+Style[^*]*\*+([^/*][^*]*\*+)*\//m;

/** Represents options to parse selectors from a string value. */
export class SelectorParseOptions {
  clearAttrs?: boolean;
  clearIds?: boolean;
  clearPseudos?: boolean;
}

/** Represents the simple selector with parsed parts. */
export class ParsedSelector {
  selector: string = '';
  tagName: string = null;
  classNames: string[] = [];
  idName: string = null;
}

/** Represents a parsed @import rule. */
export class ParsedImport {
  link: string;
  mediaQueries: string[];
}

/* Names of base mediums */
export const defaultMedium = '';
export const tabletMedium = 'tablet';
export const mobileMedium = 'mobile';
export const printMedium = 'print';

/** Medium's parents key-map: key - medium, value - its title. */
const mediumTitles: Dictionary<string> = {
  [defaultMedium]: 'Default',
  [tabletMedium]: 'Tablet',
  [mobileMedium]: 'Mobile',
  [printMedium]: 'Print',
}

/** Medium's parents key-map: key -  medium, value - its parent medium. */
const parentMediums: Dictionary<string> = {
  [defaultMedium]: undefined,
  [tabletMedium]: defaultMedium,
  [mobileMedium]: tabletMedium,
  [printMedium]: defaultMedium
}

/** Provides a bunch of static methods to work with CSS structure. */
export class CssUtils {

  /** Parses css content to node's tree.
   * Uses the 'postcss-scss' plugin that supports inline comments to parse css content.
   * @param content The css content to parse.
   * @param normalize Whether to normalize the css content.
   */
  public static parse(content: string, normalize?: boolean): Root {
    if (content === null || typeof content === 'undefined')
      return null;
    const root = scss.parse(content, { map: true });
    if (!normalize) {
      return root
    }
    // write the stylesheet type for feature validation
    const type = this.stylesheetType(content);
    root.raws.type = type;
    // normalize rile ':root' for branding stylesheet
    if (type === StylesheetType.Branding) {
      this.normalizeRule(root, ':root');
    }
    return root;
  }

  /** Generates stylesheet text for the specified root node. */
  public static cssText(root: Root): string {
    return this.generate(root, 'full', true);
  }

  /** Generates inner text for the specified css node.
   * @param cssNode The css node.
   * @returns Inner text generated for the specified css node.
   */
  public static cssInnerText(node: AnyNode): string {
    return this.generate(node, 'inner');
  }

  /** Extracts all @import links from the specified stylesheet root node. */
  public static extractImports(root: Root): ParsedImport[] {
    const imports: ParsedImport[] = [];
    for (const node of root.nodes) {
      // Skip comments - they don't affect validity
      if (node.type === 'comment') {
        continue;
      }

      // @charset is allowed and doesn't invalidate imports
      if (node.type === 'atrule' && (node as AtRule).name === 'charset') {
        continue;
      }

      // Handle @import rules
      if (node.type === 'atrule' && (node as AtRule).name === 'import') {
        const parsed = this.parseImportParams(node.params);
        if (parsed)
          imports.push(parsed);
        continue;
      }

      // Any other node type invalidates further @import rules
      break;
    }
    return imports;
  }

  /** Generates css text for the specified css node.
   * @param css The css node to generate stylesheet text.
   * @param innerContent The flag specifying whether to get inner or full css text of the given node.
   * @param restoreInlineComments Pass 'true' to save inline comment formatting, otherwise inline comments would be formatted as block comments.
   */
  public static generate(css: AnyNode, type: 'full' | 'inner' = 'full', restoreInlineComments: boolean = false): string {
    const innerContent = type === 'inner';
    let content = '';
    if (css) {
      stringify(css, (part: string, node?: AnyNode, partType?: 'start' | 'end') => {
        if (innerContent && partType)
          return;
        if (restoreInlineComments && this.isInlineComment(node)) {
          content += this.stringifyInlineComment(node as Comment);
          return;
        }
        content += part;
      });
    }
    return content;
  }

  /** Validate the given stylesheet content.
   * @param content: The stylesheet content to validate.
   * @returns An Error if the validation fail, otherwise null.
  */
  public static validate(content: string): Error {
    let error = null;
    try {
      content = this.updateInlineComments(content);
      error = this.validateSyntax(content);
    }
    catch (err) {
      error = err;
    }
    if (error) {
      // handle PostCss error
      if (error.name === 'CssSyntaxError') {
        return this.createSyntaxError(error['reason'], error['line'], error['column']);
      }
      // handle CssTree error
      if (error.name === 'SyntaxError') {
        return this.createSyntaxError(error.message, error['line'], error['column']);
      }
    }
    return error;
  }

  /** Make sure the stylesheet contains single instance of the specified rule. */
  public static normalizeRule(root: Root, selector: string) {
    let rule = this.aggregateRules(root, selector);
    if (!rule) {
      // create a new root rule if missing
      rule = this.addRule(root, selector);
    }
    this.setLastChildSemicolon(rule, true);
  }

  /** Gets the type of the stylesheet.
   * @param content: The stylesheet content.
   * @returns The stylesheet type.
   */
  public static stylesheetType(content: string): StylesheetType {
    const brandingDeclaration = this.brandingDeclaration(content);
    if (brandingDeclaration) {
      return StylesheetType.Branding;
    }
    const tableDeclaration = this.tableDeclaration(content);
    if (tableDeclaration) {
      return StylesheetType.Table;
    }
    return StylesheetType.General;
  }

  /** Returns medium title. */
  public static mediumTitle(medium: string): string {
    return mediumTitles[medium] ?? medium;
  }

  /** Returns medium's parent. */
  public static parentMedium(medium: string): string {
    if (!medium)
      return undefined;
    const parent = parentMediums[medium.toLowerCase()];
    if (typeof parent === 'string')
      return parent;
    return defaultMedium;
  }

  /** Gets the medium and all its parents for the specified medium. */
  public static getMediums(medium: string): string[] {
    const mediums: string[] = [];
    while (medium !== undefined) {
      mediums.push(medium);
      medium = this.parentMedium(medium);
    }
    return mediums;
  }

  /** Gets the Flare css meta comment from the given stylesheet content.
   * @param content: The stylesheet content.
   * @returns The Flare css meta comment if found, otherwise null.
  */
  public static metaDeclaration(content: string): string | null {
    return cssMetaRegExp.exec(content)?.[0] ?? null;
  }

  /** Gets the Flare branding comment from the given stylesheet content.
   * @param content: The stylesheet content.
   * @returns The Flare branding comment if found, otherwise null.
  */
  public static brandingDeclaration(content: string): string | null {
    return brandingRegExp.exec(content)?.[0] ?? null;
  }

  /** Gets the Flare table comment from the given stylesheet content.
   * @param content: The stylesheet content.
   * @returns The Flare table comment if found, otherwise null.
  */
  public static tableDeclaration(content: string): string | null {
    return tableRegExp.exec(content)?.[0] ?? null;
  }

  /** Gets a value of the specified declaration.
   * @param rule The rule containing the specified declaration.
   * @param name The declaration name.
   * @returns A value of the specified declaration if found, otherwise undefined.
   */
  public static getDeclarationValue(rule: Rule, name: string): string | null {
    const declaration = this.getFirstDeclaration(rule, name);
    return declaration?.value;
  }

  /** Sets a value of the specified declaration.
   * @param rule: The rule containing the specified declaration.
   * @param name The declaration name.
   * @param value The declaration value.
   */
  public static setDeclarationValue(rule: Rule, name: string, value: string) {
    let declaration = this.getFirstDeclaration(rule, name);
    if (!declaration) {
      const lastNode = rule.last;
      declaration = new Declaration({ prop: name, value: value });
      rule.append(declaration);
      // if previous node is inline comment
      if (this.isInlineComment(lastNode))
        // insert a new line char before added declaration
        rule.last.raws.before = '\n';
    }
    declaration.value = value;
  }

  /** Updates declarations of the specified css rule.
   * @param rule The css rule to update properties values.
   * @param map The name-value map that defines which declarations to update.
   */
  public static updateDeclarations(rule: Rule, map: Dictionary<string>) {
    if (!rule || !map) return rule;
    // iterate all declarations
    rule.each(node => {
      if (node.type === 'decl') {
        const value = map[node.prop];
        if (value === undefined) return;
        // update the declaration
        node.value = value;
      }
    });
    return rule;
  }

  /** Removes the specified declaration and its comments from the css rule.
   * @param rule The rule containing the specified declaration.
   * @param name The removing declaration name.
   */
  public static removeDeclaration(rule: Rule, name: string) {
    const declaration = this.getFirstDeclaration(rule, name);
    if (declaration) {
      const nodesToRemove: ChildNode[] = [declaration];
      // find comments before node and add them to list to remove
      const commentsBefore = this.getNodeCommentsPlacedBefore(declaration);
      if (commentsBefore)
        nodesToRemove.push(...commentsBefore);
      // find comments after node and add them to list to remove
      const commentAfter = this.getInlineCommentAfterNode(declaration);
      if (commentAfter)
        nodesToRemove.push(commentAfter);
      // remove declaration and its comments
      nodesToRemove.forEach(node => node.remove());
    }
  }

  /** Gets an array of css rules that contain the given selector.
   * @param root The root node to search css rules.
   * @param selector The css selector.
   * @returns The array of css rules.
   */
  public static findRulesBySelector(root: Root, selector: string): Rule[] {
    const rules: Rule[] = [];
    root.each(node => {
      if (node.type === 'rule' && (node.selector === selector || node.selectors.includes(selector))) {
        rules.push(node);
      }
    });
    return rules;
  }

  /** Aggregates declarations from all rules with the specified selector in a single rule and returns it.
   * @param root The root node that contains observable rules.
   * @param selector The selector to find and aggregate rules.
   * @returns A rule with aggregated declarations if any matched rule is found, otherwise returns null.
   */
  public static aggregateRules(root: Root, selector: string): Rule | null {
    let rules = this.findRulesBySelector(root, selector);
    if (!rules.length) {
      return null;
    }
    let rule: Rule;
    // init major rule
    {
      // find the first rule with the specified selector (exact matching)
      rule = rules.find(node => node.selector === selector);
      if (!rule) {
        // create a new rule if not found
        rule = this.addRule(root, selector);
      } else {
        // remove duplicate declarations in the major rule
        this.removeDuplicateDeclarations(rule);
        // exclude the found rule from rules array
        rules = rules.filter(item => item !== rule);
      }
    }
    // merge the remaining rules into the main rule
    if (rules.length) {
      this.mergeRules(rules, rule, true);
    }
    return rule;
  }

  /**
   * Gets all unique selectors from the specified root node.
   * @param root The root node to get selectors.
   * @returns An array of unique selectors.
   * */
  public static getAllSelectors(root: Root): string[] {
    const selectors: string[] = [];
    root.each(node => {
      if (node.type === 'rule') {
        selectors.push(...node.selectors);
      }
    });
    return Array.from(new Set(selectors));
  }

  /** Gets style names from the specified selector.
   * @param selector The selector to get styles.
   * @returns An array of styles.
   */
  public static selectorToStyleNames(selector: string, options: SelectorParseOptions = null): string[] {
    return this.parseSelector(selector, options).map(s => s.selector);
  }

  /** Parses selectors from the specified string.
   * @param selector The string value with selector expression.
   * @param options The parse options.
   * @returns The array of parsed selectors.
   */
  public static parseSelector(selector: string, options: SelectorParseOptions = null): ParsedSelector[] {
    if (!selector?.trim())
      return [];

    const ast = cssTree.parse(selector, { context: 'selector' });

    const selectors: ParsedSelector[] = [];
    let current = new ParsedSelector();

    ast.children.forEach((node) => {
      switch (node.type) {
        case 'TypeSelector':
          current.selector += node.name;
          current.tagName = node.name;
          break;
        case 'ClassSelector':
          current.selector += '.' + node.name;
          current.classNames.push(node.name);
          break;
        case 'IdSelector':
          if (!options?.clearIds) {
            current.selector += cssTree.generate(node);
            current.idName = node.name;
          }
          break;
        case 'AttributeSelector':
          if (!options?.clearAttrs) {
            current.selector += cssTree.generate(node);
          }
          break;
        case 'PseudoClassSelector':
        case 'PseudoElementSelector':
          if (!options?.clearPseudos) {
            current.selector += cssTree.generate(node);
          }
          break;
        case 'Combinator':
          if (current.selector) {
            selectors.push(current);
          }
          current = new ParsedSelector();
          break;
      }
    });

    if (current.selector) {
      selectors.push(current);
    }

    return selectors;
  }

  /** Checks whether the specified style name matches to the provided selector.
   * @param selector The simple selector that describes the document node including its tag name, id name and applied CSS classes, e.g. `p#nodeId.class1.class2`.
   * @param styleName The CSS style to check to. It can include tag name, id name and CSS classes.
   * @returns True if the specified style name matches to the provided selector, otherwise false.
   * @example `p.class1`, `p.class2`, `.class1`, `.class2` styles matches to the `p.class1.class2` selector and are applicable to such an element.
   * @example `div.class1`, `p.some-class`, `h1` styles does not match to the `p.class1.class2` selector.
   */
  public static matchStyle(selector: string | ParsedSelector, styleName: string | ParsedSelector): boolean {
    const sel: ParsedSelector = (typeof selector === 'string') ? this.parseSelector(selector)[0] : selector;
    const style: ParsedSelector = (typeof styleName === 'string') ? this.parseSelector(styleName)[0] : styleName;
    if (style.tagName === '*') {
      return true;
    }
    if (style.tagName && !CssUtils.matchXmlName(style, sel.tagName)) {
      return false;
    }
    if (style.idName && style.idName !== sel.idName) {
      return false;
    }
    const matchArr = function (selArr: string[], styleArr: string[]): boolean {
      return styleArr.every(item => selArr.includes(item));
    }
    return matchArr(sel.classNames, style.classNames);
  }

  /** Checks whether the CSS selector matches to the xml tag long name. */
  public static matchXmlName(parsedSelector: ParsedSelector, xmlLongName: string): boolean {
    if (!xmlLongName) {
      return false;
    }

    const cssLongName = parsedSelector.tagName;
    const [cssNamespace, cssTagName] = cssLongName.includes('|') ? cssLongName.split('|') : [null, cssLongName];
    const [xmlNamespace, xmlTagName] = xmlLongName.includes(':') ? xmlLongName.split(':') : [null, xmlLongName];

    if (xmlNamespace !== null && cssNamespace !== null && xmlNamespace.toLowerCase() !== cssNamespace.toLowerCase()) {
      return false;
    }
    return cssTagName === xmlTagName;
  }

  /** Renames selectors in the specified selector list according to the provided rename map and optional selector prefix.
   * @selectorList The string with selector list to rename.
   * @renames The rename map in format: { [fromSelectorType: string]: { [toSelectorType: string]: { [regex: string]: string } } }, where:
   * - fromSelectorType and toSelectorType are selector types like 'ClassSelector', 'IdSelector', etc.;
   * - regex is a regular expression to match selector part to rename;
   * - string is a replacement string to rename matched selector part.
   * Rules from rename map are applied to selector parts in the selector list in order of their appearance in the map. If selector part matches several rules, then the first matched rule will be applied.
   * @selectorPrefix The optional prefix to add to each selector in the selector list.
   * @return The renamed selector list string.
   * @example
   * Given selectorList = 'h4, #id1, div > .class2', renames = { 'TypSelector': { 'ClassSelector': { '^h(\\d)$': 'heading$1' } }, 'ClassSelector': { 'ClassSelector': { '^class(\\d)$': 'renamedClass$1' } } , 'IdSelector': { 'ClassSelector': { '^id(\\d)$': 'renamedId$1' } } }, selectorPrefix = '.prefix', the result will be '.prefix .heading4, .prefix .renamedId1, .prefix div > .renamedClass2'
   * The method does not change the structure of selectors, combinators, pseudo-classes/elements, attribute selectors, etc. in the selector list, except for renaming specified selector parts and adding optional prefix.
   */
  public static renameSelectors(selectorList: string, renames: Dictionary<Dictionary<Dictionary<string>>>, selectorPrefix: string = null): string {

    const renameSelectorPart = (selectorPart: string, selectorRenames: Dictionary<string>): string | false => {
      for (const key in selectorRenames) {
        const regex = new RegExp(key);
        const value = selectorRenames[key];
        if (regex.test(selectorPart)) {
          return selectorPart.replace(RegExp(key), value);
        }
      }
      return false;
    };

    const ast = cssTree.parse(selectorList, { context: 'selectorList' });
    cssTree.walk(ast, (node, item, list) => {
      switch (node.type) {
        case 'IdSelector':
        case 'ClassSelector':
        case 'PseudoClassSelector':
        case 'PseudoElementSelector':
        case 'TypeSelector':
          {
            const typeFrom = node.type;
            const fromTypeRenames = renames[typeFrom];
            if (fromTypeRenames) {
              for (let typeTo in fromTypeRenames) {
                const toTypeRenames = fromTypeRenames[typeTo];
                const renamed = renameSelectorPart(node.name, toTypeRenames);
                if (renamed !== false) {
                  const newNode = cssTree.fromPlainObject({
                    type: typeTo,
                    name: renamed
                  });
                  const newItem = (cssTree.List as any).createItem(newNode);
                  list.replace(item, newItem);
                }
              }
            }
            break;
          }
      }
    });

    if (selectorPrefix) {
      const selectors: string[] = [];
      cssTree.walk(ast, {
        visit: 'Selector',
        enter(node) {
          const selector = cssTree.generate(node);
          selectors.push(`${selectorPrefix} ${selector}`);
        }
      });
      return selectors.join(',');
    }

    return cssTree.generate(ast);
  }

  /**
 * Converts a string to a valid CSS class name by replacing invalid characters with Unicode escape sequences.
 * @param input - The string to be converted to a valid CSS class name
 * @returns A valid CSS class name with invalid characters escaped
 */
  public static toValidCssClassName(input: string): string {
    if (!input || typeof input !== 'string') {
      return '';
    }
    return input
      .trim()
      .replace(/[^a-zA-Z0-9_\-\|]/g, char => // replace all characters except allowed ones to Unicode escape sequence
        `\\${char.codePointAt(0)!.toString(16).toUpperCase().padStart(6, '0')}`);
  }

  /** Gets a css property key-link map from the specified css text.
   * Links are absolute inside the project, like 'Content/Resources/images/Logo.png'
   * @param rule The css rule from which to get links.
   * @param base The base absolute path relative to resolve links.
   * @returns The css property key-link map found in the css text.
   */
  public static getLinks(rule: Rule, base: string): Dictionary<string> {
    const links: Dictionary<string> = {};
    if (rule) {
      rule.each(node => {
        if (node.type === 'decl') {
          // try to get link for the css property
          const link = this.getLink(node, base);
          if (link) {
            // put link into the map
            links[node.prop] = link;
          }
        }
      });
    }
    return links;
  }

  /** Updates links in css text using provided 'updateLink' function. */
  public static updateLinksInCss(cssText: string, updateLink: (link: string) => string): string {
    const root = this.parse(cssText);
    root.walkDecls((decl) => {
      decl.value = this.updateLinksInValue(decl.value, updateLink);
    });
    const text = this.generate(root, 'full', true);
    return text;
  }

  /** Parses passed css value as link. Returns the parsed link if success, otherwise returns null. */
  public static parseValueAsLink(value: string): string {
    if (!value) return null;
    // in some cases Flare makes backslashes in links,
    // so we need to normalize them before parsing,
    // because css-tree does not support backslashes in urls
    value = replaceBackslashes(value).trim();
    const data = this.parseValue(value);
    const node = data?.children.size === 1 ? data.children.first : null;
    return this.parseNodeAsLink(node);
  }

  /** Updates links in the css property value using specified `updateLink()` function.
   * @returns The updated css property value.
   */
  public static updateLinksInValue(value: string, updateLink: (link: string) => string): string {
    if (!value?.includes('url('))
      return value;
    // in some cases Flare makes backslashes in links,
    // so we need to normalize them before parsing,
    // because css-tree does not support backslashes in urls
    value = replaceBackslashes(value).trim();
    const data = this.parseValue(value);
    if (data) {
      data.children.forEach((node) => {
        if (node.type === 'Url' && node.value) {
          const link = updateLink(node.value);
          node.value = this.encodeUrl(link);
        }
      });
      return cssTree.generate(data);
    }
    return value;
  }

  /** Encodes the specified link and wraps it into css 'url()' function. */
  public static toCssUrl(link: string, encode: boolean = true): string {
    if (encode)
      link = this.encodeUrl(link);
    return `url('${link}')`;
  }

  /**
   * Wraps the specified css text in @scope rule with provided root and limit selectors to limit the scope of css rules.
   * @param value The css text to wrap in scope.
   * @param rootSelector The root selector to use in @scope rule.
   * @param limitSelector The optional limit selector to use in @scope rule. If not specified, the scope will not be limited.
   * @returns The css text wrapped in @scope rule.
   */
  public static wrapInScope(value: string, rootSelector: string, limitSelector?: string): string {
    return `@scope (${rootSelector})${limitSelector ? ` to (${limitSelector})` : ''} {\n${value}\n}`;
  }

  /** Creates a new rule with specified selector and append it to the root node.
   * @param root The root node.
   * @param selector The new rule selector.
   * @returns The created rule.
   */
  private static addRule(root: Root, selector: string): Rule {
    const rule = new Rule({ selector });
    root.append(rule);
    return rule;
  }

  /** Gets the first declaration with specified name from the given css rule.
   * @param Rule The css rule.
   * @param name The declaration name.
   * @returns The declaration if found, otherwise undefined.
   */
  private static getFirstDeclaration(rule: Rule, name: string): Declaration {
    const nodes = rule.nodes;
    for (let i = 0; i < nodes.length; i++) {
      const node = nodes[i];
      if (node.type === 'decl' && node.prop === name) {
        return node;
      }
    }
  }

  /** Checks whether node is an inline comment. */
  private static isInlineComment(node: AnyNode): boolean {
    return node ? node.type === 'comment' && node.raws.inline === true : false;
  }

  /** Transforms inline comments in css content into block comments. */
  private static updateInlineComments(cssText: string): string {
    if (cssText?.includes('//')) {
      // parse content, scss plugin inside parse() function will updates inline comments automatically
      const root = this.parse(cssText);
      // stringify parsed css
      return this.generate(root);
    }
    return cssText;
  }

  /** Validates syntax of css text. */
  private static validateSyntax(cssText: string): Error {
    let error = null;
    try {
      cssTree.parse(cssText, {
        onParseError(err) {
          error = error || err;
        }
      });
    }
    catch (err) {
      error = error || err;
    }
    return error;
  }

  /** Removes duplicate declarations in the specified rule. */
  private static removeDuplicateDeclarations(rule: Rule) {
    const declarations: Dictionary<Declaration> = {};
    const nodesToRemove: ChildNode[] = [];
    rule.each(node => {
      if (node.type !== 'decl') return;
      const prev = declarations[node.prop];
      // if previous declaration exists it means the current declaration is duplicate
      if (prev) {
        // copy value from current declaration into previous one
        prev.value = node.value;
        // add current declaration to list to remove
        nodesToRemove.push(node);
        // copy comments
        const copiedComments = this.copyNodeComments(node, prev);
        // add copied comments to list to remove
        if (copiedComments)
          nodesToRemove.push(...copiedComments);
      } else {
        declarations[node.prop] = node;
      }
    });
    nodesToRemove.forEach(node => node.remove());
  }

  /** Gets inline comment node placed after the specified node. If comment does not exist, then return null. */
  private static getInlineCommentAfterNode(node: ChildNode): ChildNode {
    const nextNode = node.next();
    return nextNode && nextNode.type === 'comment' && nextNode.raws.inline && !nextNode.raws.before?.includes('\n')
      ? nextNode : null;
  }

  /** Copy source node comments to target node.
   * @param source - The source node whose comments should be copied.
   * @param target - The target node to copy comments to.
   * @param type - The type of comments to copy: placed before/after the source node or all comments.
   * @return Returns an array of copied source comment nodes or null when no comments to copy.
   */
  private static copyNodeComments(source: ChildNode, target: ChildNode, type: 'all' | 'before' | 'after' = 'all'): ChildNode[] {
    const copiedNodes: ChildNode[] = [];
    if (type === 'all' || type === 'before') {
      // copy comments placed before current node
      const commentsBefore = this.getNodeCommentsPlacedBefore(source);
      if (commentsBefore) {
        commentsBefore[0].raws.before = target.raws.before;
        target.raws.before = this.getLastLine(target.raws.before);
        this.insertClonedNodes(commentsBefore, target, true);
        // add comments to list to remove
        copiedNodes.push(...commentsBefore);
      }
    }
    if (type === 'all' || type === 'after') {
      // copy comments placed after current node
      const commentAfter = this.getInlineCommentAfterNode(source);
      if (commentAfter) {
        this.insertClonedNodes([commentAfter], target, false);
        // add comments to list to remove
        copiedNodes.push(commentAfter);
      }
    }
    return array(copiedNodes);
  }

  /** Gets an array of node's comments placed before it. Returns null when no node's comments. */
  private static getNodeCommentsPlacedBefore(node: ChildNode): ChildNode[] {
    const comments: ChildNode[] = [];
    for (; ;) {
      if (node.raws.before && (node.raws.before.match(/\n/g) || []).length > 1)
        /* break if node has at least one empty row before,
          e.g. \*general comment*\ is not a node comment
          =============================
          \* general comment *\

          \* node comment *\
          <node>
          ==============================
        */
        break;
      const prevNode = node.prev();
      if (!prevNode || prevNode.type !== 'comment')
        /* break if node is not a comment,
          e.g. <previous node> is not a node comment
          =============================
          <previous node>
          <node>
          ==============================
        */
        break;
      if (this.isInlineComment(prevNode) && !prevNode.raws.before?.includes('\n'))
        /* break if the comment is a inline comment and it has a node in the same line before itself,
          e.g. '// previous node comment' is not a node comment
          =============================
          <previous node> // previous node comment
          <node>
          ==============================
        */
        break;
      node = prevNode;
      comments.push(prevNode);
    }
    // reverse the array since items are added in reverse order
    return array(comments.reverse());
  }

  /** Clones the specified nodes and inserts them before/after the target node.
 * @param nodes The array of nodes to clone and insert.
 * @param target The node to insert nodes before.
 * @param before True - inserts nodes before target, False - inserts nodes after target.
 */
  private static insertClonedNodes(nodes: ChildNode[], target: ChildNode, before: boolean) {
    if (!before)
      nodes = [...nodes].reverse();
    nodes.forEach(comment => {
      const clone = comment.clone();
      if (before)
        target.parent.insertBefore(target, clone);
      else
        target.parent.insertAfter(target, clone);
    });
  }

  /** Merges an array of css rules into the specified target rule. */
  private static mergeRules(sourceRules: Rule[], targetRule: Rule, move: boolean) {
    if (!sourceRules.length) return;
    // init a declaration dictionary for the target rule
    const targetDeclarations: Dictionary<Declaration> = {};
    targetRule.walk((node) => {
      if (node.type === 'decl')
        targetDeclarations[node.prop] = node;
    });
    // merge declarations
    sourceRules.forEach(rule => {
      const selectorMatched = move && rule.selector === targetRule.selector;
      const removeFromSource = move && selectorMatched;
      this.mergeDeclarations(rule, targetRule, targetDeclarations, removeFromSource);
      if (move) {
        if (selectorMatched) {
          // remove the source rule if it has no child nodes
          let hasChildren = false;
          rule.each(node => {
            if (node.type !== 'comment') {
              hasChildren = true;
              return false;
            }
          });
          if (!hasChildren) {
            // copy rule's comments
            const comments = this.copyNodeComments(rule, targetRule, 'before');
            // remove source rule
            rule.remove();
            // remove comments of source rule
            if (comments)
              comments.forEach(comment => comment.remove());
          }
        } else {
          // remove selector from the source rule
          const selectors = rule.selectors.filter(s => s !== targetRule.selector);
          rule.selector = selectors.join('\n ');
        }
      }
    });
  }

  /** Merges declarations with comments around it from the source css rule into the target css rule.
   * @source The source css rule.
   * @target The target css rule.
   * @targetDeclarations The declaration dictionary for the target rule to speed up declaration search.
   * @removeFromSource Indicates whether moved declarations and its comments should be removed from source rule.
   */
  private static mergeDeclarations(source: Rule, target: Rule, targetDeclarations: Dictionary<Declaration>, removeFromSource: boolean) {
    source.each((node) => {
      if (node.type !== 'decl') return;
      let decl = targetDeclarations[node.prop];
      // move declaration
      {
        if (decl) {
          // update value of the exiting declaration
          decl.value = node.value;
        } else {
          // or copy the declaration from the source rule to the target rule
          decl = targetDeclarations[node.prop] = node.clone();
          target.append(decl);
        }
      }
      // copy comments to target declaration
      const copiedComments = this.copyNodeComments(node, decl);
      // remove source nodes and its comments
      if (removeFromSource) {
        const movedNodes: ChildNode[] = [node];
        if (copiedComments)
          movedNodes.push(...copiedComments);
        movedNodes.forEach(node => node.remove());
      }
    });
  }

  /** Returns substring starting from last \n char if exists, otherwise return the whole string. */
  private static getLastLine(text: string): string {
    if (!text)
      return text;
    const idx = text.lastIndexOf('\n');
    if (idx <= 0)
      return text;
    return text.substring(idx);
  }

  /** Returns text representation of the inline comment.  */
  private static stringifyInlineComment(node: Comment): string {
    return '//' + node.raws.left + node.text + node.raws.right;
  }

  /** Enables or disables a semicolon after last child of the specified css node.
   * @param cssNode The css node to enable or disable a semicolon after its last child.
   * @param enabled Specifies whether the last node's child should contain a semicolon.
   */
  private static setLastChildSemicolon(cssNode: AnyNode, enabled: boolean): void {
    cssNode.raws.semicolon = enabled;
  }

  /** Creates a syntax error. */
  private static createSyntaxError(message: string, line: number, column: number): Error {
    const error = Error(`(${line}:${column}) ${message}`);
    error.name = 'SyntaxError';
    return error;
  }

  /** Gets project absolute link path from the css property value.
   * @param styles Style declaration which contains property.
   * @param base Base absolute path relative to resolve the link path.
   * @returns The property project absolute link path.
   * @example 'url(../Images/Logo.png)' => 'Content/Resources/Images/Logo.png'
   */
  private static getLink(property: Declaration, base: string): string {
    const value = property.value.trim();
    if (!value) return null;
    // we suppose the only link in the value
    const link = this.parseValueAsLink(value);
    // do not resolve absolute links
    if (!link || isAbsoluteUrl(link)) return null;
    return resolvePath(base, link);
  }

  /** Encodes apostrophes in the specified link. */
  private static encodeUrl(link: string): string {
    return link?.replace(/\'/g, '%27');
  }

  /** Decodes apostrophes in the specified link. */
  private static decodeUrl(link: string): string {
    return link?.replace(/%27/g, '\'');
  }

  /** Returns a decoded node's value if the specified node has 'Url' type. Otherwise, returns null. */
  private static parseNodeAsLink(node: cssTree.CssNode): string | null {
    return (node?.type === 'Url') ? this.decodeUrl(node.value) : null;
  }

  /** Parses parameters of the import atrule from the specified text. */
  private static parseImportParams(text: string): ParsedImport {
    let link: string = '';
    let mediaQueries: string[] = [];
    // parse the @import atrule
    const atrule = cssTree.parse(`@import ${text};`, { context: 'atrule' });
    // extract link and media queries only (ignore other parameters)
    atrule.prelude.children.forEach((node) => {
      if (node.type === 'Url' || node.type === 'String') {
        // url
        link = node.value;
      } else if (node.type === 'MediaQueryList') {
        // media query list
        const mediaQueriesString = cssTree.generate(node);
        mediaQueries = mediaQueriesString.split(',').map(mq => mq.trim());
      }
    });
    return { link, mediaQueries };
  }

  private static parseValue(value: string): cssTree.Value {
    const data = cssTree.parse(value, { context: 'value' });
    return data.type === 'Value' ? data : null;
  }

}
